import { useEffect, useState } from 'react';

/**
 * Поиск людей по нику с паузой в наборе — один на все окна, где он есть (аудит 2026-10-17, UX-44).
 *
 * <p><b>Зачем.</b> Поиск был написан пять раз, и в четырёх из пяти ошибка запроса превращалась в
 * пустой список: `catch(() => setResults([]))`. Офлайн человек видел «никого не найдено» и решал,
 * что такого ника нет. Здесь ошибка — отдельное состояние, а «Повторить» повторяет именно поиск,
 * сразу, без паузы.
 *
 * <p>Логика — в {@link createPeopleSearch}, без React: её проверяют тесты с подменёнными
 * таймерами. Хук {@link usePeopleSearch} — тонкая обёртка для экранов.
 */

export type PeopleSearchState<T> = {
  /** Строка как набрана — для поля ввода. */
  query: string;
  /** Запрос поставлен или идёт. */
  busy: boolean;
  results: T[];
  /** Последний запрос упал; текст для человека — `humanizeError(error, t('searchError'))`. */
  error: unknown;
};

export type PeopleSearch<T> = {
  setQuery: (query: string) => void;
  /** Повторить поиск по текущей строке — немедленно. */
  retry: () => void;
  /** Пустая строка, без результатов и ошибки — окно открыли заново или выбрали человека. */
  reset: () => void;
  /** Снять поставленный запрос; опоздавший ответ уже не попадёт в состояние. */
  dispose: () => void;
  getState: () => PeopleSearchState<T>;
};

/** Пауза в наборе: запрос на каждую букву перегружал бы сеть и ограничитель частоты на сервере. */
export const PEOPLE_SEARCH_DELAY_MS = 350;

export function emptyPeopleSearchState<T>(): PeopleSearchState<T> {
  return { query: '', busy: false, results: [], error: null };
}

export function createPeopleSearch<T>(opts: {
  search: (query: string) => Promise<T[]>;
  onChange: (state: PeopleSearchState<T>) => void;
  delayMs?: number;
  /** Короче — не ищем вовсе (пересылка ищет людей с двух букв). */
  minChars?: number;
}): PeopleSearch<T> {
  const delayMs = opts.delayMs ?? PEOPLE_SEARCH_DELAY_MS;
  const minChars = opts.minChars ?? 1;
  let state = emptyPeopleSearchState<T>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** Номер последнего запроса: ответ старого запроса не перезаписывает новый. */
  let generation = 0;

  function emit(next: PeopleSearchState<T>) {
    state = next;
    opts.onChange(state);
  }

  function cancelPending() {
    if (timer) clearTimeout(timer);
    timer = null;
    generation += 1;
  }

  function run(trimmed: string) {
    const mine = ++generation;
    void opts.search(trimmed).then(
      (results) => {
        if (mine !== generation) return;
        emit({ ...state, busy: false, results, error: null });
      },
      (error: unknown) => {
        if (mine !== generation) return;
        emit({ ...state, busy: false, results: [], error: error ?? new Error('search failed') });
      },
    );
  }

  function schedule(immediate: boolean) {
    cancelPending();
    const trimmed = state.query.trim();
    if (trimmed.length < minChars) {
      emit({ ...state, busy: false, results: [], error: null });
      return;
    }
    emit({ ...state, busy: true, error: null });
    if (immediate) {
      run(trimmed);
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      run(trimmed);
    }, delayMs);
  }

  return {
    setQuery: (query) => {
      state = { ...state, query };
      schedule(false);
    },
    retry: () => schedule(true),
    reset: () => {
      cancelPending();
      emit(emptyPeopleSearchState<T>());
    },
    dispose: cancelPending,
    getState: () => state,
  };
}

/**
 * {@link createPeopleSearch} для экрана. `search` берётся при первом рендере и дальше не
 * меняется: это функция API-клиента, а фильтровать выдачу (уже участники, сам человек) экран
 * должен при показе — список «кого исключить» меняется чаще, чем идёт запрос.
 */
export function usePeopleSearch<T>(
  search: (query: string) => Promise<T[]>,
  opts?: { delayMs?: number; minChars?: number },
): PeopleSearchState<T> & Pick<PeopleSearch<T>, 'setQuery' | 'retry' | 'reset'> {
  const [state, setState] = useState<PeopleSearchState<T>>(emptyPeopleSearchState);
  const [controller] = useState(() =>
    createPeopleSearch<T>({
      search,
      onChange: setState,
      delayMs: opts?.delayMs,
      minChars: opts?.minChars,
    }),
  );
  useEffect(() => () => controller.dispose(), [controller]);
  return { ...state, setQuery: controller.setQuery, retry: controller.retry, reset: controller.reset };
}
